<template>
  <div class="w-full h-full bg-white">
    <div class="flex flex-col h-full w-[50%] p-4">
      <div>
        <div class="font-bold">{{ $t('pages.users') }}</div>
        <div class="my-2 font-bold">
          <button
            class="p-2 rounded-lg text-theme-600 hover:bg-theme-200"
            @click="isModified ? (modalCloseWarningActive = true) : onBack()">
            <IconArrowLeft />
          </button>
          {{ $t('pages.user-details') }}
        </div>
        <div class="text-sm">{{ $t('settings.userDetailInfo') }}</div>
      </div>
      <div class="flex-1 mt-4" v-if="userDetails">
        <div class="flex w-full gap-4">
          <div class="w-full">
            <label for="name" class="text-sm font-medium">{{ $t('settings.userLastName') }}</label>
            <BaseInput
              color="base"
              class="rounded-md text-theme-700"
              v-model="userDetails.name"
              :disabled="!userDetails.isFromManager"
              show-error-as-tooltip
              :error="$v.name.required.$invalid ? $v.name.required.$message : ''">
            </BaseInput>
          </div>
          <div class="w-full">
            <label for="name" class="text-sm font-medium">{{
              $t('qst-transaction-master.userName')
            }}</label>
            <BaseInput
              color="base"
              class="rounded-md text-theme-700"
              v-model="userDetails.userName"
              :disabled="!userDetails.isFromManager"
              show-error-as-tooltip
              :error="$v.userName.required.$invalid ? $v.userName.required.$message : ''">
            </BaseInput>
          </div>
        </div>
        <div class="flex w-full gap-4 mt-4">
          <div class="w-full">
            <BaseSelectDropdown
              :items="dropdownUserTypes"
              :selected-item="selectedUserType"
              :label="$t('auth.userType')"
              :disable="!userDetails.isFromManager"
              @update:selected="handleSelection"
              clearable
              show-error-as-tooltip
              :error="$v.userType.required.$invalid ? $v.userType.required.$message : ''"
              name="userType" />
          </div>
          <div class="w-full">
            <label for="email" class="text-sm font-medium">{{ $t('auth.email') }}</label>
            <BaseInput
              color="base"
              class="rounded-md text-theme-700"
              v-model="userDetails.eMail"
              :disabled="!userDetails.isFromManager"
              show-error-as-tooltip
              :error="$v.email.email.$invalid ? $v.email.email.$message : ''">
            </BaseInput>
          </div>
        </div>
        <div class="flex w-full gap-4 mt-4">
          <div class="w-full">
            <label for="phone1" class="text-sm font-medium">{{ $t('telephone') }}</label>
            <BaseInput
              id="phone1"
              color="base"
              class="rounded-md text-theme-700"
              v-model="userDetails.phone1"
              :disabled="!userDetails.isFromManager"
              show-error-as-tooltip
              :error="$v.phone1.numeric.$invalid ? $v.phone1.numeric.$message : ''">
            </BaseInput>
          </div>
          <div class="w-full">
            <div class="flex flex-col h-full">
              <label for="isActive" class="text-sm font-medium">{{
                $t('baseObject.isActive')
              }}</label>
              <BaseToggle
                :label="$t('baseObject.isActive')"
                name="isActive"
                class="flex-1"
                v-model="userDetails.isActive"
                :disable="!userDetails.isFromManager" />
            </div>
          </div>
        </div>
        <div class="pt-4">
          <label for="name" class="text-sm font-medium">{{ $t('settings.roles') }}</label>
          <div class="flex items-center mt-2">
            <BaseCheckbox
              v-model="userDetails.isAdmin"
              class="pr-2"
              :disabled="!userDetails.isFromManager || !isCurrentUserAdmin" />
            <span
              class="my-auto text-sm font-normal"
              :class="
                userDetails.isFromManager || isCurrentUserAdmin
                  ? 'cursor-pointer'
                  : 'pointer-events-none'
              "
              @click="userDetails.isAdmin = !userDetails.isAdmin"
              >{{ $t('settings.administrator') }}</span
            >

            <TooltipButton :tooltip-text="$t('settings.adminRoleOverwriteInfo')">
              <template #buttonContent>
                <button
                  class="items-center w-6 h-6 ml-2 text-sm text-gray-500 bg-gray-200 border-2 border-white border-solid rounded-full">
                  i
                </button>
              </template>
            </TooltipButton>
          </div>
        </div>
        <div class="flex gap-2 pt-4">
          <div v-for="item in roles" :key="item.id">
            <BaseButton
              :disabled="userDetails?.isAdmin || !userDetails.isFromManager || !isCurrentUserAdmin"
              @click="
                userDetails?.isAdmin || !userDetails.isFromManager ? '' : handleRoleChange(item)
              "
              :outline="isOutline(item)"
              :color="isGhostColor(item) ? 'ghost' : 'primary'"
              size="xs"
              class="p-2 truncate"
              >{{ item.name }}
            </BaseButton>
          </div>
        </div>
        <div class="w-1/2 pt-4">
          <label for="name" class="text-sm font-medium">{{ $t('traderSites.companySite') }}</label>
          <BaseMultiSelectDropdown
            :listItems="dropdownCompanySites"
            class="mt-2"
            v-model="selectedCompanies"
            :disabled="
              userDetails?.isAdmin || !userDetails.isFromManager || !isCurrentUserAdmin
            "></BaseMultiSelectDropdown>
        </div>

        <div class="flex items-center gap-2 mt-4">
          <BaseButton
            v-if="userDetails.isFromManager"
            size="sm"
            class="gap-2 capitalize"
            :disabled="!userDetails.isFromManager"
            @click="onSave">
            {{ $t('common.actions.save') }}
          </BaseButton>
          <BaseButton
            size="sm"
            outline
            class="gap-2 capitalize"
            @click="isModified ? (modalCloseWarningActive = true) : onBack()">
            {{ $t('common.actions.cancel') }}
          </BaseButton>
        </div>
      </div>
    </div>
    <BaseDrawerConfirm
      :is-active="modalCloseWarningActive"
      @confirm="onBack"
      @cancel="modalCloseWarningActive = false" />
  </div>
</template>
<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import IconArrowLeft from '@/assets/icons/IconArrowLeft.vue';
import BaseToggle from '@/components/common/BaseToggle.vue';
import BaseInput from '@/components/common/BaseInput.vue';
import BaseSelectDropdown from '@/components/common/BaseSelectDropdown.vue';
import BaseCheckbox from '@/components/common/BaseCheckbox.vue';
import BaseButton from '@/components/common/BaseButton.vue';
import TooltipButton from '@/components/common/TooltipButton.vue';
import BaseDrawerConfirm from '@/components/common/BaseDrawerConfirm.vue';
import BaseMultiSelectDropdown from '@/components/common/BaseMultiSelectDropdown.vue';

import { addOrEditUser, getAllRoles, getUserById } from '@/services/api/user';
import { useRoute, useRouter } from 'vue-router';
import type { UserDetail } from '@/types/UserDetails';
import type { SelectDropdownItem } from '@/types/Base';
import type { Role } from '@/types/Roles';
import { storeToRefs } from 'pinia';
import { useLookupsStore } from '@/stores/lookups';
import { v4 as uuidv4 } from 'uuid';
import { useToastStore } from '@/stores/toast';
import { useI18n } from 'vue-i18n';
import { email, helpers, required } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { deepCopy } from '@/utils/data';

const isCurrentUserAdmin = ref(false);

const userDetails = ref<UserDetail | null>(null);
const roles = ref<Role[] | null>(null);
const selectedUserType = ref<SelectDropdownItem | null>(null);
const { dropdownUserTypes, dropdownCompanySites } = storeToRefs(useLookupsStore());
const { getUserTypesLookup, getCompanySitesLookup, reloadLookups } = useLookupsStore();

const route = useRoute();
const router = useRouter();
const originalData = ref<UserDetail>();
const selectedCompanies = ref<any[]>();
const modalCloseWarningActive = ref(false);
const toastStore = useToastStore();
const { t: $t } = useI18n({ useScope: 'global' });

const isModified = computed(() => {
  return JSON.stringify(userDetails.value) != JSON.stringify(originalData.value);
});

function isGhostColor(item: Role) {
  return (
    userDetails.value?.isAdmin ||
    (!userDetails.value?.isFromManager &&
      !userDetails.value?.userRoles
        .filter((ur) => !ur.deleted)
        .map((ur) => ur.roleID)
        .includes(item.id))
  );
}

function isOutline(item: Role) {
  return (
    !userDetails.value?.isFromManager ==
      userDetails.value?.userRoles
        .filter((ur) => !ur.deleted)
        .map((ur) => ur.roleID)
        .includes(item.id) && !userDetails.value?.isAdmin
  );
}

onMounted(async () => {
  if (route.params.id) {
    const res = await getUserById(route.params.id as string);
    if (res) {
      userDetails.value = res;
    } else {
      userDetails.value = {
        id: route.params.id as string,
        name: '',
        userName: '',
        userRoles: [],
        userCompanySites: [],
        mwUserTypeID: '',
        isAdmin: false,
        isActive: true,
        isFromManager: true,
      };
    }
    originalData.value = deepCopy<UserDetail>(userDetails.value);
    selectedCompanies.value = userDetails.value?.userCompanySites.map((item) => {
      return { value: item.companySiteID };
    });
  }
  initDropdowns();
  isCurrentUserAdmin.value = JSON.parse(localStorage.getItem('sessionDetails')!).isAdmin;
  roles.value = await getAllRoles();
});

async function initDropdowns() {
  await getUserTypesLookup();
  const userTypesValue = dropdownUserTypes.value.find(
    (item) => item.value == userDetails.value?.mwUserTypeID
  );
  if (userTypesValue) selectedUserType.value = userTypesValue;
  await getCompanySitesLookup();
}

function handleSelection(item: SelectDropdownItem) {
  selectedUserType.value = item ? item : null;
  if (userDetails.value) userDetails.value.mwUserTypeID = item ? item.value.toString() : '';
}
function handleRoleChange(role: Role) {
  if (!userDetails.value) return;
  let index = userDetails.value?.userRoles.findIndex((ur) => {
    return ur.roleID == role.id;
  });
  let originalIndex = originalData.value?.userRoles.findIndex((ur) => {
    return ur.roleID == role.id;
  });
  if (index != -1 && originalIndex != -1 && originalIndex !== undefined && index !== undefined) {
    userDetails.value.userRoles[index].deleted = !userDetails.value.userRoles[index].deleted;
  } else if (index != -1 && index !== undefined && originalIndex == -1) {
    userDetails.value.userRoles.splice(index, 1);
  } else {
    let newRole = {
      id: uuidv4(),
      isActive: true,
      userID: userDetails.value.id,
      roleID: role.id,
      deleted: false,
    };

    userDetails.value.userRoles.push(newRole);
  }
}

function handleCompanySiteChange() {
  if (!userDetails.value) return;

  userDetails.value.userCompanySites.forEach((item) => {
    if (!selectedCompanies.value?.map((sc) => sc.value).includes(item.companySiteID)) {
      item.deleted = true;
    }
  });

  selectedCompanies.value?.forEach((company) => {
    if (
      !userDetails.value?.userCompanySites.map((uc) => uc.companySiteID).includes(company.value)
    ) {
      if (!userDetails.value) return;
      let newComapnySite = {
        id: uuidv4(),
        isActive: true,
        userID: userDetails.value.id,
        companySiteID: company.value,
        deleted: false,
      };
      userDetails.value.userCompanySites.push(newComapnySite);
    }
  });
}

function handleAdminChange() {
  if (!userDetails.value) return;
  if (userDetails.value.isAdmin) {
    userDetails.value.userRoles.map((role) => {
      role.deleted = true;
    });
  }
}
async function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  if (!userDetails.value) return;
  handleCompanySiteChange();
  handleAdminChange();
  const res = await addOrEditUser(userDetails.value);
  if (res) {
    toastStore.success({ text: $t('common.toast.success') });
    router.push('/settings/user-management');
    reloadLookups(['users']);
  } else {
    toastStore.error({ text: $t('common.toast.error') });
  }
}
function onBack() {
  router.push({ name: 'user-management' });
}

const validationModel = ref({
  name: userDetails.value?.name || '',
  userName: userDetails.value?.userName || '',
  userType: userDetails.value?.mwUserTypeID || '',
  email: userDetails.value?.eMail || '',
  phone1: userDetails.value?.phone1 || '',
});

const rules = computed(() => ({
  name: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  userName: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  userType: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  email: { email: helpers.withMessage($t('messages.errors.invalidEmail'), email) },
  phone1: {
    numeric: helpers.withMessage($t('messages.errors.invalidValue'), helpers.regex(/^[0-9]*$/)),
  },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });

watch(
  () => userDetails.value,
  (newData: any) => {
    validationModel.value.name = newData.name || '';
    validationModel.value.userName = newData.userName || '';
    validationModel.value.userType = newData.mwUserTypeID || '';
    validationModel.value.email = newData.eMail || '';
    validationModel.value.phone1 = newData.phone1 || '';
  },
  { deep: true }
);
</script>
